// Turns our raw image mask into a pretty image mask by mapping its boring
// shades-of-black greyscale to new colors. It does this using 2 textures, one
// for the raw mask and another for a color map. The color of each pixel from
// the raw mask is changed to its corresponding color from the map.
//
// The color map in a 1x256 texture where pixel (0, n) is the color to display
// wherever the raw mask has the value n.

// Put together using learning from https://webglfundamentals.org

import { SubscriptionManager } from '@sama/rxjs-core';
import { Subject } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { getAnimationFrameScheduler } from '../../../cross-team-shared/misc-utils';
import { PartialRawMask } from '../../shared/state/abstract-sensor-content-state-2d';

/**
 * A "vertex shader" computes the position of each vertex of the things you want
 * to draw. We will be drawing 2 triangles (which form a rectangle to cover the
 * entire canvas). So this will be called 6 times, once for each corner of 2
 * triangles.
 *
 * This translates coordinates in the image mask to clip space coordinates. In
 * WebGL the "clip space" is the area that will be drawn. Its coordinates go from
 * (-1, -1) to (1, 1). (There is also a z dimension, but we don't use it.)
 *
 * The coordinate space for a texture goes from (0, 0) to (1, 1). We accept that
 * here simply to pass it along to the vertex shader, where it is actually used.
 */
const vertexShaderSource = `
  // "attributes" are the inputs to a vertex shader.
  attribute vec2 a_unscaledCoordinate;
  attribute vec2 a_textureCoordinate;

  // "uniforms" are global variables
  uniform vec2 u_resolution;

  // "varyings" are extra outputs from the vertex shader (which can then be
  // used as inputs to the fragment shader).
  varying vec2 v_textureCoordinate;

  void main() {
    vec2 clipSpaceCoordinate = a_unscaledCoordinate / u_resolution * 2.0 - 1.0;

    // "gl_Position" is the magic name for the output of a vertex shader
    gl_Position = vec4(clipSpaceCoordinate * vec2(1, -1), 0, 1);

    // just pass this on to the fragment shader
    v_textureCoordinate = a_textureCoordinate;
  }
`;

/**
 * A "fragment shader" is called once for each pixel to compute its color.
 */
const fragmentShaderSource = `
  precision mediump float;

  uniform sampler2D u_mask;
  uniform sampler2D u_colorMap;

  varying vec2 v_textureCoordinate;

  void main() {
    float code = texture2D(u_mask, v_textureCoordinate).r;

    // "gl_FragColor" is the magic name for the output of a fragment shader
    gl_FragColor = texture2D(u_colorMap, vec2(0, code));

    // uncomment this to see the color map instead (for debugging)
    // gl_FragColor = texture2D(u_colorMap, v_textureCoordinate);
  }
`;

const MASK_TEXTURE = 0;
const COLOR_MAP_TEXTURE = 1;

// We need to throttle to at most 30fps because agents experience huge lag when
// working with 4k images
const RASTER_THROTTLE_TIME = 1000 / 30;

export class ColorMapPainter {
  private context: WebGLRenderingContext;
  private program: WebGLProgram;
  private throttledPaint$ = new Subject<boolean>();

  constructor(
    canvas: HTMLCanvasElement,
    private imageWidth: number,
    private imageHeight: number,
    subscriptionManager: SubscriptionManager,
  ) {
    if (!('WebGLRenderingContext' in window)) {
      // Throwing this error instead of waiting to get our common "Cannot read
      // property 'createProgram' of null" from below, so that we can
      // narrow down the cause.
      // https://www.khronos.org/webgl/wiki/FAQ#What_is_the_recommended_way_to_initialize_WebGL.3F
      throw new Error('WebGL is not supported on browser');
    }

    // The webgl context has two buffers, the one being drawn on and the one
    // being displayed. The browser updates the display either by copying or
    // swapping the buffers. `preserveDrawingBuffer: true` says to always copy,
    // which we want so the image data and the image value of
    // `canvas.toDataURL()` always match what you see on the canvas
    const gl = (this.context = canvas.getContext('webgl', {
      preserveDrawingBuffer: true,
    })!);

    this.program = this.compileProgramFromSource(gl);
    gl.useProgram(this.program);

    const { width, height } = this.setWidthHeight(gl);

    // set `a_unscaledCoordinate` to cover the whole image
    this.initializeRectangularAttribute('a_unscaledCoordinate', width, height);

    // set `a_textureCoordinate` to cover the whole texture
    this.initializeRectangularAttribute('a_textureCoordinate', 1, 1);

    // don't force us to word-align texture data
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

    this.createTexture('u_mask', MASK_TEXTURE);
    this.createTexture('u_colorMap', COLOR_MAP_TEXTURE);

    this.subscribeToThrottledPaint(subscriptionManager);
  }

  clear() {
    const gl = this.context;
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  updateMaskAndPaint(rawMask: Uint8Array) {
    const gl = this.context;
    this.loadTexture(
      MASK_TEXTURE,
      gl.LUMINANCE,
      this.imageWidth,
      this.imageHeight,
      rawMask,
    );
    this.throttledPaint$.next(true);
  }

  updatePartialMaskAndPaint(partialRawMask: PartialRawMask) {
    const gl = this.context;
    this.loadPartialTexture(MASK_TEXTURE, gl.LUMINANCE, partialRawMask);
    this.throttledPaint$.next(false);
  }

  updateColorMapAndPaint(colorMap: Uint8Array) {
    const gl = this.context;
    this.loadTexture(COLOR_MAP_TEXTURE, gl.RGBA, 1, 256, colorMap);
    this.throttledPaint$.next(true);
  }

  private setWidthHeight(gl: WebGLRenderingContext) {
    const width = gl.canvas.width;
    const height = gl.canvas.height;
    gl.viewport(0, 0, width, height);
    gl.uniform2f(this.getUniformLocation('u_resolution'), width, height);
    return { width, height };
  }

  private compileProgramFromSource(gl: WebGLRenderingContext) {
    const program = ensureNotNull(gl.createProgram());
    this.addShader(program, gl.VERTEX_SHADER, vertexShaderSource);
    this.addShader(program, gl.FRAGMENT_SHADER, fragmentShaderSource);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      throw new Error('Could not create program');
    }
    return program;
  }

  private subscribeToThrottledPaint(subscriptionManager: SubscriptionManager) {
    let debounceTime = RASTER_THROTTLE_TIME;

    // We don't debounce rendering in test environments in order to avoid having
    // to add calls to .tick() everywhere
    if (!environment.shouldDebounceRendering) {
      debounceTime = 0;
    }

    subscriptionManager.subscribeTo(
      this.throttledPaint$.pipe(
        // throttled for a minimum of 33ms, then until the animation frame is
        // available
        auditTime(debounceTime, getAnimationFrameScheduler()),
      ),
      (clear) => {
        if (clear) {
          this.clear();
        }
        this.paint();
      },
    );
  }

  private paint() {
    const gl = this.context;
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private addShader(program: WebGLProgram, type: number, source: string) {
    const gl = this.context;
    const shader = ensureNotNull(gl.createShader(type));
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      throw new Error('Could not create shader');
    }

    gl.attachShader(program, shader);
  }

  // eslint-disable-next-line max-lines-per-function -- if you need to modify this code, fix this first
  private initializeRectangularAttribute(
    name: string,
    width: number,
    height: number,
  ) {
    const gl = this.context;
    const buffer = ensureNotNull(gl.createBuffer());
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);

    // WebGL can draw dots, lines, and triangles. We can use 2 of the latter to
    // form a rectangle. So in the end we will call WebGL's `drawArrays()`,
    // which will iterate over any arrays we set up like this treating each
    // sextuplet of numbers as the definition of a triangle to draw.
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        // triangle 1
        0,
        0,
        width,
        0,
        0,
        height,

        // triangle 2
        0,
        height,
        width,
        0,
        width,
        height,
      ]),
      gl.STATIC_DRAW,
    );

    // Specify how to get data out of the buffer
    const location = gl.getAttribLocation(this.program, name);
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(
      location,
      2, // 2 components per iteration
      gl.FLOAT, // the data are 32bit floats
      false, // don't normalize the data
      // move forward size*sizeof(type) each iteration to get the next position
      0,
      0, // start at the beginning of the buffer
    );
  }

  private createTexture(uniformName: string, textureUnit: number) {
    const gl = this.context;
    gl.activeTexture(gl.TEXTURE0 + textureUnit);
    gl.uniform1i(this.getUniformLocation(uniformName), textureUnit);

    const texture = ensureNotNull(gl.createTexture());
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  }

  // eslint-disable-next-line max-params-unless-injectable -- if you need to modify this code, fix this first
  private loadTexture(
    textureUnit: number,
    format: number,
    width: number,
    height: number,
    data: ArrayBufferView,
  ) {
    const gl = this.context;
    gl.activeTexture(gl.TEXTURE0 + textureUnit);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      format,
      width,
      height,
      0,
      format,
      gl.UNSIGNED_BYTE,
      data,
    );
  }

  private loadPartialTexture(
    textureUnit: number,
    format: number,
    partialRawMask: PartialRawMask,
  ) {
    const gl = this.context;
    gl.activeTexture(gl.TEXTURE0 + textureUnit);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      partialRawMask.xOffset,
      partialRawMask.yOffset,
      partialRawMask.width,
      partialRawMask.height,
      format,
      gl.UNSIGNED_BYTE,
      partialRawMask.data,
    );
  }

  private getUniformLocation(name: string) {
    return ensureNotNull(this.context.getUniformLocation(this.program, name));
  }
}

function ensureNotNull<T>(thing: T): Exclude<T, null> {
  if (thing === null) {
    throw new Error('It was null :(');
  } else {
    return thing as Exclude<T, null>;
  }
}
